/**
 * StatusFilter Component
 * Popover-based select-style multi-select filter for order statuses
 * Dynamically shows only the statuses that exist in the current dataset
 * Allows filtering orders by multiple status values
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 2 - Updated for Dynamic Status Filtering
 */

import * as React from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { OrderStatus } from '@/types/order';

// ============================================
// CONSTANTS
// ============================================

/**
 * Status configuration with display labels and colors
 * Maps each status to proper display name and styling
 */
const STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; badgeClass: string }> = {
  ORDER_INITIALIZED: {
    label: 'Order Initialized',
    color: 'text-slate-700',
    badgeClass: 'bg-slate-100 text-slate-800 border-slate-300',
  },
  PENDING: {
    label: 'Pending',
    color: 'text-yellow-700',
    badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  },
  CONFIRMED: {
    label: 'Order Confirmed',
    color: 'text-blue-700',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-300',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    color: 'text-purple-700',
    badgeClass: 'bg-purple-100 text-purple-800 border-purple-300',
  },
  READY: {
    label: 'Ready',
    color: 'text-indigo-700',
    badgeClass: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  },
  TRANSPORT_REQUESTED: {
    label: 'Transport Requested',
    color: 'text-orange-600',
    badgeClass: 'bg-orange-100 text-orange-800 border-orange-300',
  },
  IN_TRANSIT: {
    label: 'In Transit',
    color: 'text-orange-700',
    badgeClass: 'bg-orange-100 text-orange-800 border-orange-300',
  },
  DELIVERED: {
    label: 'Delivered',
    color: 'text-emerald-700',
    badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  },
  AWAITING_CUSTOMER_APPROVAL: {
    label: 'Awaiting Approval',
    color: 'text-cyan-700',
    badgeClass: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  },
  COMPLETED: {
    label: 'Completed',
    color: 'text-green-700',
    badgeClass: 'bg-green-100 text-green-800 border-green-300',
  },
  CANCELLED: {
    label: 'Cancelled',
    color: 'text-red-700',
    badgeClass: 'bg-red-100 text-red-800 border-red-300',
  },
  DISPATCHED: {
    label: 'Dispatched',
    color: 'text-pink-700',
    badgeClass: 'bg-pink-100 text-pink-800 border-pink-300',
  },
};

// ============================================
// INTERFACE
// ============================================

export interface StatusFilterProps {
  selectedStatuses: OrderStatus[];
  onStatusChange: (statuses: OrderStatus[]) => void;
  availableStatuses: OrderStatus[];
  isLoadingStatuses?: boolean;
  className?: string;
}

// ============================================
// COMPONENT
// ============================================

export const StatusFilter = React.memo(function StatusFilter({
  selectedStatuses,
  onStatusChange,
  availableStatuses,
  isLoadingStatuses = false,
  className,
}: StatusFilterProps) {
  const [open, setOpen] = React.useState(false);

  // Handle individual status toggle
  const handleStatusToggle = React.useCallback(
    (status: OrderStatus) => {
      const isSelected = selectedStatuses.includes(status);
      const newStatuses = isSelected
        ? selectedStatuses.filter((s) => s !== status)
        : [...selectedStatuses, status];
      onStatusChange(newStatuses);
    },
    [selectedStatuses, onStatusChange]
  );

  // Handle select all
  const handleSelectAll = React.useCallback(() => {
    if (selectedStatuses.length === availableStatuses.length) {
      onStatusChange([]);
    } else {
      onStatusChange([...availableStatuses]);
    }
  }, [selectedStatuses.length, availableStatuses, onStatusChange]);

  const isAllSelected = selectedStatuses.length === availableStatuses.length;
  const isIndeterminate = selectedStatuses.length > 0 && selectedStatuses.length < availableStatuses.length;

  // Get display text
  const displayText = React.useMemo(() => {
    if (selectedStatuses.length === 0) return 'All Status';
    if (selectedStatuses.length === 1) {
      const status = selectedStatuses[0];
      return STATUS_CONFIG[status].label;
    }
    return `${selectedStatuses.length} Selected`;
  }, [selectedStatuses]);

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label */}
      <label
        htmlFor="status-filter"
        className="text-sm font-medium text-foreground"
      >
        Status
      </label>

      {/* Popover Select */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id="status-filter"
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label="Filter by status"
            disabled={isLoadingStatuses}
            className={cn(
              'flex h-9 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'transition-colors'
            )}
          >
            <span className="truncate">{displayText}</span>
            <div className="flex items-center gap-1">
              {isLoadingStatuses && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </div>
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[280px] p-0"
          align="start"
          side="bottom"
        >
          <div className="flex flex-col">
            {/* Loading State */}
            {isLoadingStatuses ? (
              <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading statuses...
              </div>
            ) : availableStatuses.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                No statuses available
              </div>
            ) : (
              <>
                {/* Select All Option */}
                <div
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 cursor-pointer',
                    'hover:bg-accent hover:text-accent-foreground',
                    'border-b transition-colors'
                  )}
                  onClick={handleSelectAll}
                  role="option"
                  aria-selected={isAllSelected}
                >
                  <Checkbox
                    checked={isAllSelected}
                    className={cn(
                      'data-[state=checked]:bg-primary data-[state=checked]:border-primary',
                      isIndeterminate && 'data-[state=indeterminate]:bg-primary'
                    )}
                    aria-label="Select all statuses"
                  />
                  <div className="flex flex-1 items-center justify-between">
                    <span className="font-medium">
                      {isAllSelected ? 'Deselect All' : 'Select All'}
                    </span>
                    {isAllSelected && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                </div>

                {/* Individual Status Options - Scrollable Container */}
                <div className="max-h-[280px] overflow-y-auto">
                  {availableStatuses.map((status) => {
                    const config = STATUS_CONFIG[status];
                    const isSelected = selectedStatuses.includes(status);

                    return (
                      <div
                        key={status}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 cursor-pointer',
                          'hover:bg-accent hover:text-accent-foreground',
                          'transition-colors'
                        )}
                        onClick={() => handleStatusToggle(status)}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <Checkbox
                          checked={isSelected}
                          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                          aria-label={`Filter by ${config.label}`}
                        />
                        <div className="flex flex-1 items-center justify-between gap-2">
                          <Badge
                            variant="outline"
                            className={cn(
                              'rounded-full px-2.5 py-0.5 text-xs font-medium border',
                              config.badgeClass
                            )}
                          >
                            {config.label}
                          </Badge>
                          {isSelected && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Accessibility hint */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {selectedStatuses.length === 0
          ? 'Showing all statuses'
          : selectedStatuses.length === 1
          ? `Filtering by ${STATUS_CONFIG[selectedStatuses[0]].label} only`
          : `Filtering by ${selectedStatuses.length} statuses`}
      </span>
    </div>
  );
});

export default StatusFilter;
